import { useApi } from '~/core/composables/useApi'

export function useSharableMembershipsCount() {
  const api = useApi()
  const { features } = useFeatures()
  const { isAuthenticated, currentCustomer } = useSessionData()
  const isMembershipSharingEnabled = features.membership_sharing

  const customerMembershipsList = ref([])
  const sharedCustomerMemberships = useTeamUpSessionStorage('shared-customer-memberships', [])
  const shareableCustomerMemberships = useTeamUpSessionStorage('shareable-customer-memberships', [])

  async function fetchShareableCustomerMemberships() {
    const customerMembershipList = await api.listCustomerMemberships({
      queryParameters: {
        fields: ['id', 'membership.name', 'membership.shareable'],
        expand: ['membership'],
      },
    })

    const filteredMembershipListResults = customerMembershipList.results
      .filter((membership: any) => membership.membership.shareable)
      .map((membership: any) => ({
        id: membership.id,
        name: membership.membership.name,
      }))

    customerMembershipsList.value = filteredMembershipListResults
  }

  async function getCustomerMembershipSharingStatus(membership: any) {
    const sharingStatus = await api.getCustomerMembershipSharingStatus(membership.id)
    const isMembershipShared = sharingStatus.every((status: any) => status.shared)

    return !isMembershipShared && {
      id: membership.id,
      name: membership.name,
      share_status: sharingStatus,
    }
  }

  async function fetchCustomerMembershipSharingStatuses() {
    if (!isMembershipSharingEnabled) {
      shareableCustomerMemberships.value = []

      return
    }

    let statuses

    if (shareableCustomerMemberships.value.length === 0) {
      statuses = await Promise.all(
        customerMembershipsList.value.map(getCustomerMembershipSharingStatus),
      )

      statuses.forEach((status, index) => {
        if (status?.share_status) {
          shareableCustomerMemberships.value.push({ ...status })
        }
        else {
          sharedCustomerMemberships.value.push({ ...customerMembershipsList.value[index] })
        }
      })
    }
    else {
      customerMembershipsList.value.forEach((membership) => {
        const sharedList = sharedCustomerMemberships.value.find((sharedMembership: any) => sharedMembership.id === membership.id)
        const shareableList = shareableCustomerMemberships.value.find((shareableMembership: any) => shareableMembership.id === membership.id)

        if (!sharedList && !shareableList) {
          shareableCustomerMemberships.value.push(membership)
        }
      })

      statuses = await Promise.all(
        shareableCustomerMemberships.value.map(getCustomerMembershipSharingStatus),
      )
    }
  }

  const sharableMembershipsCount = computed(() => {
    if (!isMembershipSharingEnabled) {
      return 0
    }
    return shareableCustomerMemberships.value.length
  })

  onMounted(async () => {
    if (!isAuthenticated || !currentCustomer) {
      return
    }

    await fetchShareableCustomerMemberships()
    await fetchCustomerMembershipSharingStatuses()
  })

  return {
    sharableMembershipsCount,
  }
}
